import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Package, AlertTriangle, XCircle } from 'lucide-react';
import { ProcurementItem } from '@/types/database.types';
import { ItemQualityData } from '@/types/database/procurement-item-quality';

const QUALITY_GRADES = ['A', 'B', 'C', 'Damaged', 'Mixed'];

interface ItemQualityInspectionFormProps {
  items: ProcurementItem[];
  itemQualityData: ItemQualityData[];
  setItemQualityData: React.Dispatch<React.SetStateAction<ItemQualityData[]>>;
  gradingType: 'preliminary' | 'final';
}

const ItemQualityInspectionForm: React.FC<ItemQualityInspectionFormProps> = ({
  items,
  itemQualityData,
  setItemQualityData,
  gradingType
}) => {
  const updateItemData = (itemId: string, field: keyof ItemQualityData, value: any) => {
    setItemQualityData(prev => 
      prev.map(item => {
        if (item.itemId !== itemId) return item;
        
        // Handle rejection toggle - auto-fill damage/compensation
        if (field === 'isRejected') {
          const isRejecting = value as boolean;
          if (isRejecting) {
            return {
              ...item,
              isRejected: true,
              qualityGrade: 'Damaged',
              damageKg: item.quantity,
              compensationKg: item.quantity,
              damagePercentage: 100,
              notes: item.notes || 'Item fully rejected - quality below standards'
            };
          } else {
            return {
              ...item,
              isRejected: false,
              qualityGrade: '',
              damageKg: 0,
              compensationKg: 0,
              damagePercentage: 0
            };
          }
        }
        
        return { ...item, [field]: value };
      })
    );
  };

  const calculateTotals = () => {
    return itemQualityData.reduce((acc, item) => ({
      totalDamage: acc.totalDamage + (item.damageKg || 0),
      totalCompensation: acc.totalCompensation + (item.compensationKg || 0),
      totalQuantity: acc.totalQuantity + (item.quantity || 0),
      rejectedCount: acc.rejectedCount + (item.isRejected ? 1 : 0)
    }), { totalDamage: 0, totalCompensation: 0, totalQuantity: 0, rejectedCount: 0 });
  };

  const totals = calculateTotals();
  const isPreliminary = gradingType === 'preliminary';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2">
          <Package className="h-4 w-4" />
          Per-Item Quality Inspection ({items.length} items)
        </h3>
        <div className="flex items-center gap-2">
          {totals.rejectedCount > 0 && (
            <Badge variant="destructive">
              <XCircle className="h-3 w-3 mr-1" />
              {totals.rejectedCount} rejected
            </Badge>
          )}
          <Badge variant={isPreliminary ? "secondary" : "default"}>
            {isPreliminary ? 'Preliminary' : 'Final'}
          </Badge>
        </div>
      </div>

      <div className="space-y-4">
        {itemQualityData.map((itemData, index) => {
          const isRejected = itemData.isRejected;
          
          return (
            <Card 
              key={itemData.itemId} 
              className={`border-l-4 ${isRejected ? 'border-l-destructive bg-destructive/5' : 'border-l-primary/30'}`}
            >
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">#{index + 1}</span>
                    <span className={isRejected ? 'line-through text-muted-foreground' : ''}>
                      {itemData.productName}
                    </span>
                  </span>
                  <div className="flex items-center gap-2">
                    {isRejected && (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        REJECTED
                      </Badge>
                    )}
                    <Badge variant="outline">{itemData.quantity} KG</Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {/* Reject Item Toggle */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <XCircle className={`h-4 w-4 ${isRejected ? 'text-destructive' : 'text-muted-foreground'}`} />
                    <Label htmlFor={`reject-${itemData.itemId}`} className="font-medium cursor-pointer">
                      Reject this item
                    </Label>
                  </div>
                  <Switch
                    id={`reject-${itemData.itemId}`}
                    checked={isRejected}
                    onCheckedChange={(checked) => updateItemData(itemData.itemId, 'isRejected', checked)}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Quality Grade</Label>
                    <Select
                      value={itemData.qualityGrade}
                      onValueChange={(value) => updateItemData(itemData.itemId, 'qualityGrade', value)}
                      disabled={isRejected}
                    >
                      <SelectTrigger className={`h-9 ${isRejected ? 'opacity-50' : ''}`}>
                        <SelectValue placeholder="Grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {QUALITY_GRADES.map(grade => (
                          <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {isPreliminary ? 'Est. Damage' : 'Damage'} (KG)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      className={`h-9 ${isRejected ? 'opacity-50' : ''}`}
                      value={itemData.damageKg || ''}
                      onChange={(e) => updateItemData(itemData.itemId, 'damageKg', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      disabled={isRejected}
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {isPreliminary ? 'Est. Compensation' : 'Compensation'} (KG)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      className={`h-9 ${isRejected ? 'opacity-50' : ''}`}
                      value={itemData.compensationKg || ''}
                      onChange={(e) => updateItemData(itemData.itemId, 'compensationKg', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      disabled={isRejected}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Notes</Label>
                  <Textarea
                    value={itemData.notes}
                    onChange={(e) => updateItemData(itemData.itemId, 'notes', e.target.value)}
                    placeholder={isRejected ? "Rejection reason..." : "Quality observations for this item..."}
                    rows={2}
                    className="text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Totals Summary - without Net Payable */}
      <Card className="bg-muted/50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="font-medium">Totals Summary</span>
            <div className="flex gap-4 text-sm flex-wrap">
              <span>
                <span className="text-muted-foreground">Total Qty:</span>{' '}
                <strong>{totals.totalQuantity.toFixed(1)} KG</strong>
              </span>
              <span>
                <span className="text-muted-foreground">
                  {isPreliminary ? 'Est. ' : ''}Damage:
                </span>{' '}
                <strong>{totals.totalDamage.toFixed(1)} KG</strong>
              </span>
              <span>
                <span className="text-muted-foreground">
                  {isPreliminary ? 'Est. ' : ''}Compensation:
                </span>{' '}
                <strong className="text-destructive">{totals.totalCompensation.toFixed(1)} KG</strong>
              </span>
              {totals.rejectedCount > 0 && (
                <span>
                  <span className="text-muted-foreground">Rejected Items:</span>{' '}
                  <strong className="text-destructive">{totals.rejectedCount}</strong>
                </span>
              )}
            </div>
          </div>
          {totals.totalCompensation > 0 && (
            <div className="mt-2 flex items-center gap-2 text-amber-600 text-sm">
              <AlertTriangle className="h-4 w-4" />
              <span>
                {isPreliminary ? 'Estimated ' : ''}compensation of {totals.totalCompensation.toFixed(1)} KG will be deducted from payment
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ItemQualityInspectionForm;
